import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { CraneShift } from "src/store/api/generatedApi";
import { ShiftsAction, ShiftsState } from "./types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const mergeShifts = (
  existing: CraneShift[],
  incoming: CraneShift[]
): CraneShift[] => {
  const existingTimes = new Set(existing.map((s) => s.shift_start_time));
  const newShifts = incoming.filter(
    (s) => !existingTimes.has(s.shift_start_time)
  );
  return [...existing, ...newShifts].sort(
    (a, b) =>
      new Date(a.shift_start_time).getTime() -
      new Date(b.shift_start_time).getTime()
  );
};

export const findShiftAtTime = (
  shifts: CraneShift[],
  targetTime: string | undefined
): number => {
  if (shifts.length === 0) return 0;
  if (targetTime === undefined) return shifts.length - 1;

  const target = new Date(targetTime).getTime();
  for (let i = shifts.length - 1; i >= 0; i--) {
    const shift = shifts[i];
    if (!shift) continue;
    const shiftStart = new Date(shift.shift_start_time).getTime();
    if (shiftStart <= target) {
      return i;
    }
  }
  return 0;
};

export const shiftsReducer = (
  state: ShiftsState,
  action: ShiftsAction
): ShiftsState => {
  switch (action.type) {
    case "ADD_SHIFTS": {
      if (action.fromCache) {
        if (state.shifts.length === 0) {
          // Cache data: set as stale, select based on targetTime
          return {
            ...state,
            shifts: action.shifts,
            selectedIndex: findShiftAtTime(action.shifts, state.targetTime),
            isStale: true,
          };
        }
        // API arrived before cache, merge but mark stale
        const merged = mergeShifts(state.shifts, action.shifts);
        return {
          ...state,
          shifts: merged,
          selectedIndex: findShiftAtTime(merged, state.targetTime),
          isStale: true,
        };
      }

      if (state.shifts.length === 0 || state.isStale) {
        // Fresh state or revalidating stale cache
        return {
          ...state,
          shifts: action.shifts,
          selectedIndex: findShiftAtTime(action.shifts, state.targetTime),
          isStale: false,
        };
      }

      const merged = mergeShifts(state.shifts, action.shifts);
      return {
        ...state,
        shifts: merged,
        selectedIndex: findShiftAtTime(merged, state.targetTime),
        isStale: false,
      };
    }

    case "SET_TARGET_TIME": {
      if (state.shifts.length === 0) {
        return { ...state, targetTime: action.targetTime };
      }
      return {
        ...state,
        targetTime: action.targetTime,
        selectedIndex: findShiftAtTime(state.shifts, action.targetTime),
      };
    }

    case "SELECT_PREVIOUS": {
      if (state.selectedIndex <= 0) {
        return state;
      }
      const newIndex = state.selectedIndex - 1;
      return {
        ...state,
        selectedIndex: newIndex,
        targetTime: state.shifts[newIndex]?.shift_start_time,
      };
    }

    case "SELECT_NEXT": {
      if (state.selectedIndex >= state.shifts.length - 1) {
        return state;
      }
      const newIndex = state.selectedIndex + 1;
      return {
        ...state,
        selectedIndex: newIndex,
        targetTime: state.shifts[newIndex]?.shift_start_time,
      };
    }

    default:
      return state;
  }
};
